import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { BookOpen, Users, Scroll, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JoinCampaign = () => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [playerName, setPlayerName] = useState("");
  const [characterName, setCharacterName] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInviteInfo();
  }, [inviteCode]);

  const fetchInviteInfo = async () => {
    try {
      const response = await axios.get(`${API}/invites/${inviteCode}`);
      const info = response.data;
      setInviteInfo(info);

      // Check if guest already has a session for this campaign
      const storedToken = localStorage.getItem(`guest_${info.campaign_id}`);
      if (storedToken) {
        try {
          await axios.get(`${API}/guest/check/${info.campaign_id}`, {
            headers: { "X-Guest-Token": storedToken },
          });
          // Valid session — redirect to portal
          navigate(`/campaign/${info.campaign_id}/player-portal`, { replace: true });
          return;
        } catch {
          // Token invalid — clear it and show form
          localStorage.removeItem(`guest_${info.campaign_id}`);
        }
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError("This invite link is invalid or has been deleted.");
      } else if (err.response?.status === 410) {
        setError(err.response.data.detail || "This invite has expired.");
      } else {
        setError("Failed to load invite information.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!playerName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!characterName.trim()) {
      toast.error("Please enter your character name");
      return;
    }

    setJoining(true);
    try {
      const response = await axios.post(`${API}/invites/${inviteCode}/guest-join`, {
        player_name: playerName.trim(),
        character_name: characterName.trim(),
      });

      // Store the guest token for this campaign
      localStorage.setItem(`guest_${response.data.campaign_id}`, response.data.guest_token);

      toast.success(`Welcome to ${response.data.campaign_name}!`);
      navigate(`/campaign/${response.data.campaign_id}/player-portal`, { replace: true });
    } catch (err) {
      if (err.response?.data?.detail) {
        toast.error(err.response.data.detail);
      } else {
        toast.error("Failed to join campaign");
      }
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-card border-border">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-destructive" />
            </div>
            <h2 className="text-xl font-serif text-foreground mb-2" data-testid="invite-error-title">Invalid Invite</h2>
            <p className="text-muted-foreground mb-6" data-testid="invite-error-message">{error}</p>
            <Button onClick={() => navigate("/")} className="bg-primary text-primary-foreground" data-testid="go-home-button">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="join-campaign-page">
      <Card className="max-w-md w-full bg-card border-border">
        <CardHeader className="text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Scroll className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="font-serif text-2xl">Join Campaign</CardTitle>
          <CardDescription>
            You've been invited to join{" "}
            <span className="text-primary font-medium">{inviteInfo?.campaign_name}</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {inviteInfo?.campaign_description && (
            <p className="text-muted-foreground text-sm mb-6 text-center">{inviteInfo.campaign_description}</p>
          )}
          <form onSubmit={handleJoin} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">
                Your Name
              </label>
              <Input
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Enter your real name"
                required
                data-testid="player-name-input"
                className="bg-input border-border"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">
                Character Name
              </label>
              <Input
                value={characterName}
                onChange={(e) => setCharacterName(e.target.value)}
                placeholder="Enter your character's name"
                required
                data-testid="character-name-input"
                className="bg-input border-border"
              />
              <p className="text-xs text-muted-foreground mt-1">
                This is how other players will see you in the campaign
              </p>
            </div>
            <Button
              type="submit"
              disabled={joining}
              className="w-full bg-primary text-primary-foreground"
              data-testid="join-campaign-button"
            >
              {joining ? (
                "Joining..."
              ) : (
                <>
                  Enter the Campaign
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground text-center mt-4">
            No account needed — just enter your details and you're in
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default JoinCampaign;
